import { LoadingOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Input, List, Select, Spin, Tag, Typography } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import parsePhoneNumberFromString, { isValidPhoneNumber } from 'libphonenumber-js';
import { debounce } from 'lodash';
import { Link2, Link2Off, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useAuth from '../../hooks/useAuth';
import { addContact, isPhoneNoExist, updateContact } from '../../redux/actions/contact';
import { getCustomers } from '../../redux/actions/customer';
import { sendAssignContactEmail } from '../../redux/actions/email';
import { getUsers } from '../../redux/actions/users';
import { getFullName } from '../../utils';
import CustomModal from '../CustomModal';
import CustomPhoneInput from '../CustomPhoneInput';

const { Option } = Select;

const ContactModal = ({ open, onCancel, data, isUnknownContact }) => {
    const { user } = useAuth();
    const [form] = Form.useForm();
    const [users, setUsers] = useState([]);
    const [loadingState, setLoadingState] = useState({ phone: false, users: false, customer: false });
    const [matchedCustomers, setMatchedCustomers] = useState();
    const [linkCustomer, setLinkCustomer] = useState();

    const nameValue = useWatch('name', form);

    const handleCustomerSearch = useCallback(
        debounce(async (value) => {
            if (!value.trim()) {
                setMatchedCustomers();
                return;
            }

            setLoadingState((prev) => ({ ...prev, customer: true }));

            try {
                const res = await getCustomers({ search: value, loading: false });
                if (res?.customers?.length) {
                    setMatchedCustomers(res.customers);
                } else {
                    setMatchedCustomers(null);
                }
            } finally {
                setLoadingState((prev) => ({ ...prev, customer: false }));
            }
        }, 300),
        []
    );

    const handleSearch = useCallback(
        debounce(async (value) => {
            setLoadingState((prev) => ({ ...prev, users: true }));
            const res = await getUsers({ searchText: value, loading: false, isDispatch: false });
            if (res?.ack) setUsers(res.users);
            setLoadingState((prev) => ({ ...prev, users: false }));
        }, 300),
        []
    );

    const handlePhoneSearch = useMemo(
        () =>
            debounce(async (value) => {
                setLoadingState((prev) => ({ ...prev, phone: true }));

                try {
                    const checkPhoneNo = await isPhoneNoExist(value);
                    if (checkPhoneNo?.exists) {
                        form.setFields([
                            {
                                name: 'phone',
                                errors: [checkPhoneNo?.message],
                            },
                        ]);
                    }
                } finally {
                    setLoadingState((prev) => ({ ...prev, phone: false }));
                }
            }, 500),
        [form]
    );

    const handleSubmit = async (values) => {
        values.phone = values?.phone?.phoneNumber;
        if (linkCustomer?.netsuite_customer_id)
            values.netsuite_customer_id = linkCustomer?.netsuite_customer_id;
        if (user.role === 2) values.assigned_user_id = user.id;

        const res = data ? await updateContact({ ...values, id: data?.id }) : await addContact(values);

        if (res?.ack) {
            if (values?.assigned_user_id && values?.assigned_user_id !== data?.assigned_user_id) {
                sendAssignContactEmail({ contact: values });
            }
            onCancel();
        }
    };

    useEffect(() => {
        if (user?.role !== 2) getUsers().then((res) => res?.ack && setUsers(res.users));
    }, []);

    useEffect(() => {
        if (!data) {
            form.resetFields();
            return;
        }

        let phoneRaw = data.phone ?? '';

        if (phoneRaw && !phoneRaw.startsWith('+')) {
            phoneRaw = `+${phoneRaw}`;
        }

        const parsed = parsePhoneNumberFromString(phoneRaw);

        const phoneValue = parsed
            ? {
                country: parsed.country,
                countryCode: `+${parsed.countryCallingCode}`,
                phoneNumber: parsed.number,
            }
            : undefined;

        form.setFieldsValue({
            name: data.name,
            phone: phoneValue,
            assigned_user_id: data.assigned_user_id,
        });

        if (data.netsuite_customer_id) {
            setLinkCustomer({
                id: data.customer_id,
                name: data.customer_name,
                code: data.customer_code,
                netsuite_customer_id: data.netsuite_customer_id,
            });
        } else {
            setLinkCustomer(undefined);
        }
    }, [data, form]);

    return (
        <CustomModal
            title={data ? 'Update Contact' : 'Add New Contact'}
            open={open}
            onCancel={onCancel}
            okText={`${data ? 'Update' : 'Add'} Contact`}
            onOk={() => form.submit()}
            okButtonProps={{
                disabled: !nameValue?.trim() || !!form.getFieldsError().some(({ errors }) => errors.length),
            }}
            width={500}
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                    label="Name"
                    name="name"
                    rules={[{ required: true, message: 'Please enter the name!' }]}
                >
                    <Input placeholder="John Doe" />
                </Form.Item>

                <Form.Item
                    name="phone"
                    label="Phone"
                    rules={[
                        { required: true, message: 'Please enter phone number!' },
                        () => ({
                            validator(_, value) {
                                if (!value?.phoneNumber || !value?.country) return Promise.resolve();

                                return isValidPhoneNumber(value.phoneNumber, value.country)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Invalid phone number'));
                            },
                        }),
                    ]}
                >
                    <CustomPhoneInput
                        onChange={(value) => handlePhoneSearch(value?.phoneNumber)}
                        disabled={isUnknownContact}
                    />
                </Form.Item>

                {linkCustomer ? (
                    <Form.Item label="Customer">
                        <Flex gap={10}>
                            <Input readOnly value={`(${linkCustomer?.code}) ${linkCustomer?.name}`} />
                            <Button
                                icon={<Link2Off size={14} cursor="pointer" />}
                                color="primary"
                                variant="outlined"
                                onClick={() => setLinkCustomer()}
                            >
                                Unlink
                            </Button>
                        </Flex>
                    </Form.Item>
                ) : (
                    <Form.Item label="Customer">
                        <Input
                            placeholder="Search Customer"
                            onChange={(e) => handleCustomerSearch(e.target.value)}
                        />
                    </Form.Item>
                )}

                {loadingState?.customer ? (
                    <Flex justify="center" className="mt-6" align="center" gap={10}>
                        <Typography.Text type="secondary">Loading...</Typography.Text>
                        <Spin indicator={<LoadingOutlined spin />} size="small" />
                    </Flex>
                ) : (
                    matchedCustomers && (
                        <List
                            itemLayout="horizontal"
                            header={
                                <Flex justify="space-between" align="center">
                                    <Typography.Text strong>Associated Customers</Typography.Text>
                                    <X size={18} cursor="pointer" onClick={() => setMatchedCustomers(null)} />
                                </Flex>
                            }
                            bordered
                            size="small"
                            className="associated-customer"
                            dataSource={matchedCustomers}
                            itemHeight={1}
                            renderItem={(item) => (
                                <List.Item
                                    actions={[
                                        <Button
                                            size="small"
                                            icon={<Link2 size={14} cursor="pointer" />}
                                            color="primary"
                                            variant="link"
                                            onClick={() => {
                                                setLinkCustomer(item);
                                                setMatchedCustomers();
                                            }}
                                        >
                                            Link
                                        </Button>,
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={
                                            <span>
                                                <strong>({item?.code})</strong> {item?.name}
                                            </span>
                                        }
                                        description={item?.phone}
                                    />
                                </List.Item>
                            )}
                        />
                    )
                )}

                {user.role !== 2 && (
                    <Form.Item name="assigned_user_id" label="Assign User">
                        <Select
                            placeholder="Assign User"
                            optionLabelProp="label"
                            style={{ width: '100%' }}
                            loading={loadingState.users}
                            showSearch
                            allowClear
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            onDropdownVisibleChange={(open) => {
                                if (open && !users?.length) getUsers({ loading: false });
                            }}
                            onSearch={handleSearch}
                        >
                            {users?.map((user) => (
                                <Option key={user?.id} value={user?.id} label={getFullName(user)}>
                                    <Flex vertical gap={1}>
                                        <Typography.Text>{getFullName(user)}</Typography.Text>
                                        <Typography.Text type="secondary">{user.email}</Typography.Text>
                                    </Flex>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
            </Form>
        </CustomModal>
    );
};

export default ContactModal;
